// 文档查看：https://github.com/mzabriskie/axios
import { bindSignature, encryptLoginPassword, syncServerTime } from '@tiger/api-signature';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import APP from 'stores/APP';
import security from 'stores/security';
import API from 'utils/API';
import { language, printf } from 'utils/i18n';
import { IS_UAT } from '../constants';
import { getBaggage, SUPPORT_BAGGAGE_HEADER } from '../tenant';

declare module 'axios' {
    interface AxiosRequestConfig {
        useForm?: boolean;
        useJson?: boolean;
        noToken?: boolean;
        tradeAuth?: boolean;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
    interface AxiosResponse {
        [key: string]: any;
    }
}

export default axios;

type ErrorCode = number | string;
type ErrorMsg = string;

export interface HttpError extends Error {
    error_code: ErrorCode;
    error_msg: ErrorMsg;
    response: AxiosResponse;
    config: AxiosRequestConfig;
}

interface IData {
    error_msg?: ErrorMsg;
    error_description?: ErrorMsg;
    error_message?: ErrorMsg;
    message?: ErrorMsg;
    description?: ErrorMsg;
    msg?: ErrorMsg;
    byteLength?: ErrorCode;
    error_code?: ErrorCode;
    code?: ErrorCode;
}

const ERROR_MSG = {
    /* 网络类异常 */
    OFF_LINE: __('Connection lost, please check your internet connection and try again.'),
    CANCEL: __('Request Canceled.'),

    200: __('Request Failed.'),
    401: __('Please Log In.'),
    403: __('Access Denied.'),
    413: __('File Over Maximum Size.'),
    404: __('Resource Not Found.'),
    500: __('The service is temporarily unavailable, please retry later.'),
    503: __('The service is temporarily unavailable, please retry later.')
};

function dataSerializer(data: object | string) {
    let key: string;
    const result: string[] = [];

    if (typeof data === 'string') {
        return data;
    }

    for (key in data as object) {
        if (data.hasOwnProperty(key)) {
            result.push(`${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`);
        }
    }

    return result.join('&');
}

axios.interceptors.request.use((config: AxiosRequestConfig) => {
    if (!config.timeout) {
        config.timeout = 60 * 1000;
    }

    config.params = Object.assign(
        {
            _s: Date.now(),
            lang: language,
            os: APP.os,
            platform: APP.platform,
            deviceId: APP.deviceId,
            appVer: APP.shortVersion,
            vendor: APP.vendor,
            category: APP.category,
            version: APP.version
        },
        config.params
    );

    // post/put请求时传输的密码，其他服务需要规避使用 login_password 作为字段约束，该字段是auth业务中的登录密码
    if (config.data?.login_password && !config.data.pwd_api_ver) {
        config.data.pwd_api_ver = 'v2';
        config.data.login_password = encryptLoginPassword(config.data.login_password);
    }

    // 增加对表单数组提交的支持
    if (config.useForm && (config.method === 'post' || config.method === 'put')) {
        config.headers!['Content-Type'] = 'application/x-www-form-urlencoded';
        config.transformRequest = dataSerializer;
    }

    if (!config.noToken && security.ACCESS_TOKEN) {
        config.headers!.Authorization = `Bearer ${security.ACCESS_TOKEN}`;
    }

    // 避免覆盖自定的 Baggage
    if (!config.headers!.Baggage) {
        config.headers!.Baggage = getBaggage(APP.tenantId, security.currentAccount?.entityId);
    }

    // 如果不需要多租户header，则删除
    if (!SUPPORT_BAGGAGE_HEADER) {
        delete config.headers!.Baggage;
    }

    // 需要交易token
    if (config.tradeAuth && security.TRADE_TOKEN) {
        config.headers!.TradeToken = security.TRADE_TOKEN;
    }

    if (config.url?.startsWith(API.OAUTH.origin())) {
        // 注：UAT环境使用测试环境的key/secret
        bindSignature(
            config,
            IS_UAT
                ? {
                      appKey: 'APP_TEST',
                      appSecret: 'test123'
                  }
                : undefined
        );
    }

    return config;
});

axios.interceptors.response.use((response: AxiosResponse) => {
    let data = response.data;

    const config = response.config;

    // 接口签名的时间戳要根据服务器返回的时间戳同步
    if (data?.server_time && config.url?.startsWith(API.OAUTH.origin())) {
        syncServerTime(data.server_time);
    }

    // 处理responseType: 'arraybuffer'错误
    if (
        data &&
        response.data.toString() === '[object ArrayBuffer]' &&
        response.headers![`content-type`] === 'application/json'
    ) {
        const enc = new TextDecoder('utf-8');

        data = response.data = JSON.parse(enc.decode(new Uint8Array(response.data))) || {};
    }

    if (data && typeof data === 'object') {
        if (data.is_succ === false || ('status' in data && data.status !== 'ok') || ('ret' in data && data.ret !== 0)) {
            return createError({ response } as AxiosError);
        }

        return data;
    }

    return response;
}, createError);

/**
 * @description 返回一个以包装后的error对象为拒绝原因的promise
 *
 * @return {promise}
 */
function createError(responseError: AxiosError): Promise<any> {
    let error_code!: ErrorCode;
    let error_msg!: ErrorMsg;
    let response: AxiosResponse = {} as AxiosResponse;

    const pickError = function(data: IData) {
        if (data && typeof data === 'object') {
            const msg =
                data.error_msg ||
                data.error_description ||
                data.error_message ||
                data.message ||
                data.msg ||
                data.description;
            const code = data.error_code || data.code;

            if (msg && !error_msg) {
                error_msg = msg;
            }

            if (code && !error_code) {
                error_code = code;
            }
        }
    };

    // 请求已经发送，并且服务器有返回
    if (responseError.response) {
        response = responseError.response;

        // 361 security
        if (response.status === 361 && response.headers && response.headers['verification-url']) {
            const url = response.headers['verification-url'];

            window.location.href = url;
        }

        // 接口的返回内容
        const body = response.data;

        if (body && typeof body === 'object') {
            // 有的接口错误描述还被包了一层，所以也尝试解析
            pickError(body.data);
            pickError(body);
        }

        // 如果依然没有取到错误code，这使用http code当作错误code
        if (!error_code) {
            error_code = response.status;
        }
    } else if (responseError.request) {
        // 请求已发送但是没有收到服务器响应
        if ('onLine' in navigator && navigator.onLine === false) {
            error_code = 'OFF_LINE';
        } else if (responseError.code === 'ECONNABORTED') {
            error_code = 504;

            error_msg = printf(
                __('Connection timed out(%sms), please check your internet connection and try again.'),
                String(responseError.config.timeout)
            );
        }
    } else {
        // 请求未发出
        error_msg = responseError.message;

        if (axios.isCancel(responseError)) {
            error_code = 'CANCEL';
        }
    }

    if (!error_code) {
        error_code = -1;
    }

    if (!error_msg) {
        error_msg =
            ERROR_MSG[error_code] ||
            response.statusText ||
            printf(__(`The service is temporarily unavailable(%s)`), error_code);
    }

    const error = new Error(error_msg) as HttpError;

    error.error_code = error_code;
    error.error_msg = error_msg;
    error.response = response;
    error.config = responseError.config;

    return Promise.reject(error);
}
